import {FormControl, FormGroup, UntypedFormBuilder} from '@angular/forms';
import {BillableItemType} from '@ee/common/enums';
import {ReportSettingField} from './report-field.model';
import {ReportSettingFormValue} from './report-settings.model';

export enum CheckProcessStatus {
  ANY = 'ANY',
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
  DENIED = 'DENIED'
}

export interface BillableItemsSearchForm {
  start_date: Date;
  end_date: Date;
  billable_item_type: -1 | BillableItemType;
  invoice_status: number;
  invoice_payment_status: number;
  case_status: number;
  client_id: string;
  property_manager_id: string;
  attorney_id: string;
  performed_by_user_id: string;
  // Check search fields
  check_search: boolean;
  check_process_status: CheckProcessStatus;
  check_processed_date_start?: Date;
  check_processed_date_end?: Date;
  check_number?: string;
  check_processed_by?: string;
  check_payee?: string;
  // Sorting fields
  sort_by?: string;
  sort_direction?: string;
  // Server-side pagination
  page?: number;
  page_size?: number;
  // Client tag filter
  client_tags?: string[];
}

export interface BillableItemsResult {
  billable_item_id: string;
  company_name: string;
  case_id: string;
  type: string;
  qty: number;
  rate: number;
  amount: number;
  description: string;
  incurred_date?: string;
  invoice?: string;
  invoice_payment_status?: string;
  created_by?: string;
  performed_by?: string;
  created_date?: string;
  // Check-related fields
  request_check: boolean;
  check_payee?: string;
  check_number?: string;
  check_processed_date?: string;
  check_processed_by?: string;
  /** EE-531: "EasyEviction support (on behalf of X)" for a support-session act, else the plain name. Never the email. */
  check_processed_by_display?: string | null;
  check_denied: boolean;
}

export class BillableItemsReportSettingFormValue extends ReportSettingFormValue {
  billable_item_type: -1 | BillableItemType;
  invoice_status: number;
  invoice_payment_status: number;
  case_status: number;
  client_id: string;
  property_manager_id: string;
  attorney_id: string;
  performed_by_user_id: string;
  deleted: boolean;
  check_search: boolean;
  check_process_status: CheckProcessStatus;
  check_number?: string;
  check_processed_by?: string;
  check_payee?: string;
  client_tags?: string[];
  fields: ReportSettingField[];
  column_header_overrides?: Record<string, string>;
}

export function GenerateBillableItemsSearchForm(fb: UntypedFormBuilder, startDate: Date, endDate: Date): FormGroup {
  return new FormGroup({
    start_date: new FormControl(startDate, {nonNullable: true}),
    end_date: new FormControl(endDate, {nonNullable: true}),
    billable_item_type: new FormControl<-1 | BillableItemType>(-1, {nonNullable: false}),
    invoice_status: new FormControl<0 | 1 | 2>(0, {nonNullable: true}),
    invoice_payment_status: new FormControl<0 | 1 | 2 | 3>(0, {nonNullable: true}),
    case_status: new FormControl<0 | 1 | 2>(0, {nonNullable: true}),
    client_id: new FormControl(''),
    property_manager_id: new FormControl(''),
    attorney_id: new FormControl(''),
    performed_by_user_id: new FormControl(''),
    deleted: new FormControl(false),
    // Check search fields
    check_search: new FormControl<boolean>(false, {nonNullable: true}),
    check_process_status: new FormControl<CheckProcessStatus>(CheckProcessStatus.ANY, {nonNullable: true}),
    check_processed_date_start: new FormControl<Date | null>(null),
    check_processed_date_end: new FormControl<Date | null>(null),
    check_number: new FormControl<string | null>(null),
    check_processed_by: new FormControl<string | null>(null),
    check_payee: new FormControl<string | null>(null),
    // Fields for column selection
    fields: fb.array([]),
    // Client tag filter
    client_tags: new FormControl<string[]>([]),
  });
}
